import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { extractYouTubeId, absoluteFileUrl } from "@/lib/utils_ext";
import { Play, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

export default function VideoPlayerModal({ open, onClose, video, onPlay }) {
  const [related, setRelated] = useState([]);
  useEffect(() => {
    if (!open || !video) return;
    setRelated([]);
    api.get("/related", {
      params: {
        resource_type: "video",
        class_level: video.class_level,
        subject: video.subject,
        chapter: video.chapter,
        exclude_id: video.video_id,
        limit: 4,
      },
    }).then((r) => setRelated(r.data)).catch(() => setRelated([]));
  }, [open, video]);

  if (!video) return null;
  const yt = extractYouTubeId(video.video_url);
  const src = yt ? `https://www.youtube.com/embed/${yt}?autoplay=1` : absoluteFileUrl(video.video_url);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-3xl max-w-4xl border-border p-0 overflow-hidden" data-testid="video-player-modal">
        <div className="p-5 border-b border-border">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-semibold pr-8">{video.title}</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Class {video.class_level} · {video.subject} · by {video.uploader_name}
            </DialogDescription>
          </DialogHeader>
        </div>
        <div className="aspect-video bg-black">
          {yt ? (
            <iframe src={src} title={video.title} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-testid="video-iframe" />
          ) : (
            <video src={src} controls autoPlay className="w-full h-full" data-testid="video-native" />
          )}
        </div>
        {video.description && <div className="p-5 text-sm text-muted-foreground leading-relaxed border-b border-border">{video.description}</div>}
        {related.length > 0 && (
          <div className="p-5" data-testid="video-related">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-3">
              <Sparkles className="w-3 h-3 text-primary" /> Related lessons
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {related.map((r) => (
                <button
                  key={r.video_id}
                  onClick={() => onPlay?.(r)}
                  data-testid={`related-video-${r.video_id}`}
                  className="text-left rounded-xl overflow-hidden bg-muted/30 hover:bg-muted transition-colors group"
                >
                  <div className="relative aspect-video bg-muted">
                    {r.thumbnail && <img src={r.thumbnail} alt="" className="w-full h-full object-cover" />}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors">
                      <div className="w-9 h-9 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <Play className="w-4 h-4 text-primary" fill="currentColor" />
                      </div>
                    </div>
                  </div>
                  <div className="p-2">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                      Class {r.class_level} · {r.subject}
                    </div>
                    <div className="text-xs font-semibold line-clamp-2 leading-snug mt-0.5">{r.title}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
